import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch

@Composable
fun TeamsScreen(
    token: String?,
    role: String?,
    leagueId: String,
    leagueName: String,
    seasonId: String,
    onOpenRoster: (teamId: String, teamName: String) -> Unit,
) {
    var leagueTeams by remember { mutableStateOf<List<SeasonLeagueTeamDto>>(emptyList()) }
    var availableTeams by remember { mutableStateOf<List<TeamDto>>(emptyList()) }
    var rosterCounts by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    var teamCount by remember { mutableStateOf<Int?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var info by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }

    var selectedExistingTeamId by remember { mutableStateOf("") }
    var newTeamName by remember { mutableStateOf("") }
    var newTeamAverage by remember { mutableStateOf("") }

    // Editace průměru (season_league_team.average_score, pokud existuje, jinak globální team.average_score).
    var editingTeamId by remember { mutableStateOf<String?>(null) }
    var editTeamAverage by remember { mutableStateOf("") }

    // Přejmenování se zachováním historie (viz Api.renameTeam) — samostatná akce od editace průměru.
    var renamingTeamId by remember { mutableStateOf<String?>(null) }
    var renameNewName by remember { mutableStateOf("") }

    val scope = rememberCoroutineScope()

    LaunchedEffect(refreshKey) {
        loading = true
        try {
            leagueTeams = Api.fetchLeagueTeams(token, leagueId)
            teamCount = Api.fetchLeague(token, leagueId).teamCount
            rosterCounts = Api.fetchRosterCountsForSeason(token, seasonId)
            val allTeams = Api.fetchAllTeams(token)
            val assignedInSeason = Api.fetchAssignedTeamIdsForSeason(token, seasonId)
            availableTeams = allTeams.filter { it.id !in assignedInSeason }
            error = null
        } catch (e: Throwable) {
            error = "Načtení týmů selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val isFull = teamCount != null && leagueTeams.size >= teamCount!!

    fun teamName(row: SeasonLeagueTeamDto): String = row.teamNameOverride ?: row.team?.name ?: row.teamId

    // Živě spočtená pozice nasazení -- stejné pořadí, jaké by právě teď použilo generate_schedule
    // (coalesce sezónního a globálního průměru, sestupně, nully naposled), ne uložený `seed` sloupec
    // (ten se zapíše až při příštím "Vygenerovat rozpis" a do té doby by u nově přidaného týmu nebo
    // po změně průměru zůstal neaktuální).
    val rankedTeams = remember(leagueTeams) {
        leagueTeams.sortedWith(
            compareByDescending<SeasonLeagueTeamDto> { it.averageScore ?: it.team?.averageScore ?: Double.NEGATIVE_INFINITY }
                .thenBy { it.teamId }
        )
    }

    Div(attrs = { classes("card") }) {
        H2 { Text("Týmy — liga $leagueName") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }
        info?.let { Div { Text(it) } }
        teamCount?.let { count -> Div { Text("Obsazeno: ${leagueTeams.size} / $count") } }

        Table {
            Tr {
                Th { Text("Nasazení") }
                Th { Text("Tým") }
                Th { Text("Průměr") }
                Th { Text("Soupiska") }
                Th { }
            }
            rankedTeams.forEachIndexed { index, row ->
                val seedPosition = (index + 1).toString()
                when {
                    editingTeamId == row.teamId -> {
                        Tr {
                            Td { Text(seedPosition) }
                            Td { Text(teamName(row)) }
                            Td {
                                Input(type = InputType.Text) {
                                    value(editTeamAverage)
                                    onInput { editTeamAverage = it.value }
                                }
                            }
                            Td { Text("${rosterCounts[row.teamId] ?: 0} / 6") }
                            Td {
                                Button(attrs = {
                                    onClick {
                                        val average = editTeamAverage.toDoubleOrNull()
                                        scope.launch {
                                            try {
                                                if (row.averageScore != null) {
                                                    Api.updateSeasonLeagueTeamAverage(token, row.id, average)
                                                } else {
                                                    Api.updateTeam(token, row.teamId, row.team?.name ?: teamName(row), average)
                                                }
                                                editingTeamId = null
                                                refreshKey++
                                            } catch (e: Throwable) {
                                                error = "Uložení selhalo: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }) { Text("Uložit") }
                                Button(attrs = { onClick { editingTeamId = null } }) { Text("Zrušit") }
                            }
                        }
                    }
                    renamingTeamId == row.teamId -> {
                        Tr {
                            Td { Text(seedPosition) }
                            Td {
                                Input(type = InputType.Text) {
                                    value(renameNewName)
                                    onInput { renameNewName = it.value }
                                }
                            }
                            Td { Text(row.averageScore?.toString() ?: row.team?.averageScore?.toString() ?: "—") }
                            Td { Text("${rosterCounts[row.teamId] ?: 0} / 6") }
                            Td {
                                Button(attrs = {
                                    onClick {
                                        if (renameNewName.isNotBlank()) {
                                            scope.launch {
                                                try {
                                                    Api.renameTeam(token, row.teamId, renameNewName)
                                                    renamingTeamId = null
                                                    refreshKey++
                                                } catch (e: Throwable) {
                                                    error = "Přejmenování selhalo: ${e.apiMessage()}"
                                                }
                                            }
                                        }
                                    }
                                }) { Text("Uložit") }
                                Button(attrs = { onClick { renamingTeamId = null } }) { Text("Zrušit") }
                            }
                        }
                    }
                    else -> {
                        Tr {
                            Td { Text(seedPosition) }
                            Td(attrs = { onClick { onOpenRoster(row.teamId, teamName(row)) } }) {
                                Text(teamName(row))
                            }
                            Td { Text(row.averageScore?.toString() ?: row.team?.averageScore?.toString() ?: "—") }
                            Td { Text("${rosterCounts[row.teamId] ?: 0} / 6") }
                            Td {
                                Button(attrs = { onClick { onOpenRoster(row.teamId, teamName(row)) } }) { Text("Soupiska") }
                                if (role.canManage()) {
                                    Button(attrs = {
                                        onClick {
                                            editingTeamId = row.teamId
                                            editTeamAverage = (row.averageScore ?: row.team?.averageScore)?.toString() ?: ""
                                        }
                                    }) { Text("Upravit průměr") }
                                    Button(attrs = {
                                        onClick {
                                            renamingTeamId = row.teamId
                                            renameNewName = row.team?.name ?: ""
                                        }
                                    }) { Text("Přejmenovat") }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (role.canManage()) {
            Button(attrs = {
                onClick {
                    scope.launch {
                        try {
                            Api.generateSchedule(token, leagueId)
                            info = "Rozpis vygenerován."
                            error = null
                        } catch (e: Throwable) {
                            error = "Generování rozpisu selhalo: ${e.apiMessage()}"
                        }
                    }
                }
            }) { Text("Vygenerovat rozpis") }
        }

        if (role.canManage() && isFull) {
            Div { Text("Liga je plná, další týmy nelze přiřadit.") }
        } else if (role.canManage()) {
            H3 { Text("Přiřadit existující tým (bez přiřazení v této sezóně)") }
            Div {
                Select(attrs = { onChange { selectedExistingTeamId = it.value ?: "" } }) {
                    Option("") { Text("— vyber tým —") }
                    availableTeams.forEach { team ->
                        Option(team.id) { Text("${team.name} (${team.averageScore ?: "?"})") }
                    }
                }
                Button(attrs = {
                    onClick {
                        if (selectedExistingTeamId.isNotBlank()) {
                            scope.launch {
                                try {
                                    Api.assignTeamToLeague(token, leagueId, selectedExistingTeamId)
                                    selectedExistingTeamId = ""
                                    refreshKey++
                                } catch (e: Throwable) {
                                    error = "Přiřazení selhalo: ${e.apiMessage()}"
                                }
                            }
                        }
                    }
                }) { Text("Přiřadit") }
            }

            H3 { Text("Založit nový tým") }
            Div {
                Input(type = InputType.Text) {
                    placeholder("Název týmu")
                    value(newTeamName)
                    onInput { newTeamName = it.value }
                }
                Input(type = InputType.Text) {
                    placeholder("Průměr (nepovinné)")
                    value(newTeamAverage)
                    onInput { newTeamAverage = it.value }
                }
                Button(attrs = {
                    onClick {
                        if (newTeamName.isNotBlank()) {
                            scope.launch {
                                try {
                                    val team = Api.createTeam(token, newTeamName, newTeamAverage.toDoubleOrNull())
                                    Api.assignTeamToLeague(token, leagueId, team.id)
                                    newTeamName = ""
                                    newTeamAverage = ""
                                    refreshKey++
                                } catch (e: Throwable) {
                                    error = "Založení týmu selhalo: ${e.apiMessage()}"
                                }
                            }
                        }
                    }
                }) { Text("Založit a přiřadit") }
            }
        }
    }
}
